import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { PageBuilder } from './PageBuilder';
import { DEFAULT_PAGES } from './constants';

/** Modal dialog for editing a CMS page: meta fields on top, block builder
 *  below. The builder is only mounted once the page has been saved (we need
 *  a real page.id to attach blocks to).
 *
 *  Hosted by AdminCMSPage; all state lives in the parent so the dialog
 *  remains a thin presentational shell.
 */
export function PageEditorDialog({
  open,
  onOpenChange,
  editingPage,
  pageForm,
  setPageForm,
  saving,
  savePageMeta,
  api,
  builderKey,
  onEditBlock,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); }}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4 text-[#5C7599] cursor-pointer" onClick={() => onOpenChange(false)} />
            {editingPage ? `Edit Page: ${editingPage.title}` : 'Create Page'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Page Meta */}
          <Card className="bg-[#000B18] border-white/10">
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium">Page Settings</h3>
                <Button onClick={savePageMeta} disabled={saving} size="sm" className="btn-primary" data-testid="save-page-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Page
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Page Title</Label>
                  <Input
                    value={pageForm.title}
                    onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })}
                    className="bg-[#041A3B] border-white/20 text-white"
                    placeholder="About Us"
                    data-testid="page-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">URL Slug</Label>
                  <Input
                    value={pageForm.slug}
                    onChange={(e) => setPageForm({ ...pageForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="bg-[#041A3B] border-white/20 text-white"
                    placeholder="about-us"
                    disabled={editingPage && DEFAULT_PAGES.find(d => d.slug === editingPage.slug)}
                    data-testid="page-slug-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Page Description</Label>
                <Textarea
                  value={pageForm.description}
                  onChange={(e) => setPageForm({ ...pageForm, description: e.target.value })}
                  className="bg-[#041A3B] border-white/20 text-white"
                  rows={2}
                  placeholder="Short intro shown above blocks"
                />
              </div>

              <details className="rounded border border-white/10 bg-[#041A3B]/40">
                <summary className="cursor-pointer px-3 py-2 text-white text-sm font-medium">SEO Meta (advanced)</summary>
                <div className="p-3 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#A0B4CC] text-xs">Meta Title</Label>
                    <Input
                      value={pageForm.meta_title}
                      onChange={(e) => setPageForm({ ...pageForm, meta_title: e.target.value })}
                      className="bg-[#041A3B] border-white/20 text-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#A0B4CC] text-xs">Meta Description</Label>
                    <Textarea
                      value={pageForm.meta_description}
                      onChange={(e) => setPageForm({ ...pageForm, meta_description: e.target.value })}
                      className="bg-[#041A3B] border-white/20 text-white"
                      rows={2}
                    />
                  </div>
                </div>
              </details>

              <div className="flex items-center justify-between p-3 rounded bg-[#041A3B]/60">
                <Label className="text-white">Published</Label>
                <Switch
                  checked={pageForm.is_published}
                  onCheckedChange={(checked) => setPageForm({ ...pageForm, is_published: checked })}
                  data-testid="page-published-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Block builder (only after page exists) */}
          {editingPage?.id ? (
            <PageBuilder
              pageId={editingPage.id}
              pageSlug={editingPage.slug}
              api={api}
              refreshKey={builderKey}
              onEditBlock={onEditBlock}
            />
          ) : (
            <Card className="bg-[#000B18] border-white/10 border-dashed">
              <CardContent className="py-8 text-center text-[#5C7599]">
                Save the page first to start adding blocks.
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0B4CC]">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PageEditorDialog;
